import { supabase } from './supabase'

// ============ EQUIPE ============

export async function getEquipe() {
  const { data, error } = await supabase.from('equipe').select('*').eq('actif', true).order('created_at')
  if (error) throw error
  return data
}

export async function getCurrentEquipeMember() {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null
  const { data } = await supabase.from('equipe').select('*').eq('user_id', user.id).eq('actif', true).single()
  return data
}

export async function createEquipeMember(member) {
  const { data, error } = await supabase.from('equipe').insert(member).select().single()
  if (error) throw error
  return data
}

export async function updateEquipeMember(id, updates) {
  const { data, error } = await supabase.from('equipe').update(updates).eq('id', id).select().single()
  if (error) throw error
  return data
}

export async function deleteEquipeMember(id, userId) {
  const { data, error: fnError } = await supabase.functions.invoke('invite-equipe-member', {
    body: { action: 'delete', equipe_id: id, user_id: userId || null },
  })
  if (fnError || data?.error) {
    throw new Error(data?.detail || data?.error || fnError?.message || 'Erreur suppression')
  }
}

// ============ COMMERCANTS (CRM) ============

export async function getCommercants(filters = {}) {
  let q = supabase.from('commercants').select('*, commercial:equipe(prenom, zone)').order('updated_at', { ascending: false })
  if (filters.statut) q = q.eq('statut', filters.statut)
  if (filters.zone) q = q.eq('zone', filters.zone)
  if (filters.commercial_id) q = q.eq('commercial_id', filters.commercial_id)
  if (filters.reaction) q = q.eq('reaction', filters.reaction)
  if (filters.search) q = q.ilike('nom', `%${filters.search}%`)
  const { data, error } = await q
  if (error) throw error
  return data
}

export async function getCommercant(id) {
  const { data, error } = await supabase.from('commercants').select('*, commercial:equipe(prenom, zone, email)').eq('id', id).single()
  if (error) throw error
  return data
}

export async function createCommercant(c) {
  const { data, error } = await supabase.from('commercants').insert(c).select().single()
  if (error) throw error
  return data
}

export async function updateCommercant(id, updates) {
  const { data, error } = await supabase.from('commercants').update(updates).eq('id', id).select().single()
  if (error) throw error
  return data
}

export async function deleteCommercant(id) {
  const { error } = await supabase.from('commercants').delete().eq('id', id)
  if (error) throw error
}

// ============ INTERACTIONS ============

export async function getInteractions(commercantId) {
  const { data, error } = await supabase.from('interactions').select('*, auteur:equipe(prenom)')
    .eq('commercant_id', commercantId).order('created_at', { ascending: false })
  if (error) throw error
  return data
}

export async function createInteraction(interaction) {
  const { data, error } = await supabase.from('interactions').insert(interaction).select().single()
  if (error) throw error
  return data
}

// ============ ABONNEMENTS ============

export async function getAbonnements(commercantId) {
  const { data, error } = await supabase.from('abonnements').select('*, plan:plans(nom, prix_mensuel)')
    .eq('commercant_id', commercantId).order('created_at', { ascending: false })
  if (error) throw error
  return data
}

// ============ STOCK ============

export async function getStockNiveaux() {
  const { data, error } = await supabase.from('stock_niveaux').select('*')
  if (error) throw error
  return data
}

export async function getProduits() {
  const { data, error } = await supabase.from('produits').select('*').order('nom')
  if (error) throw error
  return data
}

export async function createProduit(produit) {
  const { data, error } = await supabase.from('produits').insert(produit).select().single()
  if (error) throw error
  return data
}

export async function updateProduit(id, updates) {
  const { data, error } = await supabase.from('produits').update(updates).eq('id', id).select().single()
  if (error) throw error
  return data
}

export async function getMouvementsStock(filters = {}) {
  let q = supabase.from('mouvements_stock').select('*, produit:produits(nom), commercant:commercants(nom), responsable:equipe(prenom)')
    .order('created_at', { ascending: false }).limit(100)
  if (filters.produit_id) q = q.eq('produit_id', filters.produit_id)
  if (filters.commercant_id) q = q.eq('commercant_id', filters.commercant_id)
  const { data, error } = await q
  if (error) throw error
  return data
}

export async function createMouvement(mouvement) {
  const { data, error } = await supabase.from('mouvements_stock').insert(mouvement).select().single()
  if (error) throw error
  return data
}

// ============ FINANCES ============

export async function getMRRActuel() {
  const { data, error } = await supabase.from('mrr_actuel').select('*').single()
  if (error) return { mrr_total: 0, clients_actifs: 0, mrr_idf: 0, mrr_nice: 0, arpu: 0 }
  return data
}

export async function getFinancesMensuelles() {
  const { data, error } = await supabase.from('finances_mensuelles').select('*').order('mois', { ascending: false }).limit(12)
  if (error) throw error
  return data
}

export async function upsertFinanceMensuelle(finance) {
  const { data, error } = await supabase.from('finances_mensuelles').upsert(finance, { onConflict: 'mois' }).select().single()
  if (error) throw error
  return data
}

export async function getCommissions(filters = {}) {
  let q = supabase.from('commissions').select('*, membre:equipe(prenom, zone), commercant:commercants(nom)')
    .order('mois', { ascending: false })
  if (filters.equipe_id) q = q.eq('equipe_id', filters.equipe_id)
  if (filters.mois) q = q.eq('mois', filters.mois)
  const { data, error } = await q
  if (error) throw error
  return data
}

export async function markCommercantReviewed(id) {
  await supabase.from('commercants').update({ last_reviewed_at: new Date().toISOString() }).eq('id', id)
}

export async function getCommercantsToReview() {
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
  const { data } = await supabase
    .from('commercants')
    .select('id, nom, statut, last_reviewed_at, reaction, zone')
    .not('statut', 'in', '(churne)')
    .or(`last_reviewed_at.is.null,last_reviewed_at.lt.${weekAgo}`)
    .order('last_reviewed_at', { ascending: true, nullsFirst: true })
    .limit(10)
  return data || []
}

// ============ STATS ============

export async function getStatsCommercant(commercantId) {
  const { data, error } = await supabase.from('stats_commercant').select('*').eq('id', commercantId).single()
  if (error) return null
  return data
}

export async function getDashboardStats() {
  const [mrr, pipeline, essais, recentActivity] = await Promise.all([
    getMRRActuel(),
    getCommercants(),
    supabase.from('commercants').select('id, nom, date_fin_essai').eq('statut', 'essai_gratuit'),
    supabase.from('interactions').select('*, auteur:equipe(prenom), commercant:commercants(nom)')
      .order('created_at', { ascending: false }).limit(10)
  ])

  const allCommercants = pipeline || []
  const pipelineCounts = {}
  for (const c of allCommercants) {
    pipelineCounts[c.statut] = (pipelineCounts[c.statut] || 0) + 1
  }

  const actionsAujourdhui = allCommercants.filter(c => {
    if (!c.next_action_date) return false
    const today = new Date().toISOString().slice(0, 10)
    return c.next_action_date <= today
  })

  return {
    mrr,
    pipelineCounts,
    essais: essais.data || [],
    recentActivity: recentActivity.data || [],
    actionsAujourdhui,
    totalCommercants: allCommercants.length
  }
}

// ============ ROADMAP ============

export async function getRoadmapItems(filters = {}) {
  let q = supabase.from('roadmap').select('*, assigne:equipe(prenom), decision:decisions(decision)')
    .order('created_at', { ascending: false })
  if (filters.statut) q = q.eq('statut', filters.statut)
  if (filters.tag) q = q.eq('tag', filters.tag)
  const { data, error } = await q
  if (error) throw error
  return data
}

export async function createRoadmapItem(item) {
  const { data, error } = await supabase.from('roadmap').insert(item).select().single()
  if (error) throw error
  return data
}

export async function updateRoadmapItem(id, updates) {
  const { data, error } = await supabase.from('roadmap').update(updates).eq('id', id).select().single()
  if (error) throw error
  return data
}

// ============ TICKETS ============

export async function getTicketsByCommercant(commercantId) {
  const { data, error } = await supabase.from('tickets')
    .select('*, assigne:equipe!tickets_assigne_id_fkey(prenom)')
    .eq('commercant_id', commercantId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return data
}

export async function getTickets(filters = {}) {
  let q = supabase.from('tickets')
    .select('*, assigne:equipe!tickets_assigne_id_fkey(prenom), createur:equipe!tickets_cree_par_id_fkey(prenom), commercant:commercants(nom)')
    .order('created_at', { ascending: false })
  if (filters.statut) q = q.eq('statut', filters.statut)
  if (filters.assigne_id) q = q.eq('assigne_id', filters.assigne_id)
  if (filters.categorie) q = q.eq('categorie', filters.categorie)
  const { data, error } = await q
  if (error) throw error
  return data
}

export async function createTicket(ticket) {
  const { data, error } = await supabase.from('tickets').insert(ticket).select().single()
  if (error) throw error
  return data
}

export async function updateTicket(id, updates) {
  if (updates.statut === 'ferme') updates.closed_at = new Date().toISOString()
  const { data, error } = await supabase.from('tickets').update(updates).eq('id', id).select().single()
  if (error) throw error
  return data
}

// ============ DECISIONS ============

export async function getDecisions() {
  const { data, error } = await supabase.from('decisions').select('*').order('date_decision', { ascending: false })
  if (error) throw error
  return data
}

export async function createDecision(decision) {
  const { data, error } = await supabase.from('decisions').insert(decision).select().single()
  if (error) throw error
  return data
}

export async function updateDecision(id, updates) {
  const { data, error } = await supabase.from('decisions').update(updates).eq('id', id).select().single()
  if (error) throw error
  return data
}

// ============ JOURNAL ============

export async function getJournaux() {
  const { data, error } = await supabase.from('journal').select('*, auteur:equipe(prenom)')
    .order('date_semaine', { ascending: false })
  if (error) throw error
  return data
}

export async function createJournal(entry) {
  const { data, error } = await supabase.from('journal').insert(entry).select().single()
  if (error) throw error
  return data
}

// ============ NOTIFICATIONS ============

export async function getNotifications(limit = 50) {
  const { data, error } = await supabase.from('notifications').select('*')
    .order('created_at', { ascending: false }).limit(limit)
  if (error) throw error
  return data
}

export async function getUnreadCount() {
  const { count, error } = await supabase.from('notifications').select('*', { count: 'exact', head: true }).eq('lu', false)
  if (error) return 0
  return count
}

export async function markNotificationRead(id) {
  const { error } = await supabase.from('notifications').update({ lu: true }).eq('id', id)
  if (error) throw error
}

export async function markAllNotificationsRead() {
  const { error } = await supabase.from('notifications').update({ lu: true }).eq('lu', false)
  if (error) throw error
}

// ============ CONSTANTS ============

export const STATUTS = [
  { value: 'prospect', label: 'Prospect', color: '#94a3b8' },
  { value: 'contacte', label: 'Contact\u00e9', color: '#60a5fa' },
  { value: 'rdv_planifie', label: 'RDV planifi\u00e9', color: '#a78bfa' },
  { value: 'demo_faite', label: 'D\u00e9mo faite', color: '#f59e0b' },
  { value: 'essai_gratuit', label: 'Essai gratuit', color: '#fb923c' },
  { value: 'actif', label: 'Actif', color: '#22c55e' },
  { value: 'en_pause', label: 'En pause', color: '#eab308' },
  { value: 'churne', label: 'Churn\u00e9', color: '#ef4444' },
]

// ============ ACTIVATE COMMERCANT ============

export async function activateCommercant(commercantId, newStatut) {
  const { data: { session } } = await supabase.auth.getSession()
  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/activate-commercant`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`,
      },
      body: JSON.stringify({ commercant_id: commercantId, new_statut: newStatut }),
    }
  )
  const result = await res.json()
  if (!res.ok) throw new Error(result.detail || result.error || 'Erreur activation')
  return result
}

// ============ CHEVALETS V2 ============

export async function getChevaletsV2(filters = {}) {
  let q = supabase.from('chevalets')
    .select('*, merchant:merchants(id, name, slug, tags(id, short_code, label, active))')
    .order('id')
  if (filters.id) q = q.eq('id', filters.id)
  if (filters.status) q = q.eq('status', filters.status)
  if (filters.merchant_id) q = q.eq('merchant_id', filters.merchant_id)
  const { data, error } = await q
  if (error) throw error
  return data
}

export async function getNextChevaletId() {
  const { data, error } = await supabase.rpc('next_chevalet_id')
  if (error) throw error
  return data
}

export async function createChevaletV2(id) {
  const url_nfc = `https://taprize.app/c/${id}`
  const { data, error } = await supabase.from('chevalets')
    .insert({ id, url_nfc })
    .select()
    .single()
  if (error) throw error
  return data
}

// commercant = { id, merchant_id, nom, commercial_id, chevalet_id? }
export async function lierChevaletMarchand(chevaletId, commercant) {
  if (!commercant.merchant_id) throw new Error('merchant_id manquant sur le commercant')

  // Si ce commercant avait déjà un autre chevalet lié, le remettre en "disponible"
  if (commercant.chevalet_id && commercant.chevalet_id !== chevaletId) {
    await supabase.from('chevalets')
      .update({ merchant_id: null, status: 'disponible', installed_at: null })
      .eq('id', commercant.chevalet_id)
    // Pas de throw si cette étape échoue : non-bloquant (l'ancien chevalet peut déjà être libéré)
  }

  const { data: chevalet, error: e1 } = await supabase.from('chevalets')
    .update({ merchant_id: commercant.merchant_id, status: 'en_essai' })
    .eq('id', chevaletId)
    .select()
    .single()
  if (e1) throw e1
  const { error: e2 } = await supabase.from('commercants')
    .update({ chevalet_id: chevaletId })
    .eq('id', commercant.id)
  if (e2) throw e2
  return chevalet
}

export async function confirmerInstallationV2(chevaletId) {
  const { data, error } = await supabase.from('chevalets')
    .update({ status: 'installe', installed_at: new Date().toISOString() })
    .eq('id', chevaletId)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function libererChevalet(chevaletId) {
  // maybeSingle() évite l'erreur PGRST116 si le chevalet n'existe pas
  const { data: c, error: selectErr } = await supabase.from('chevalets')
    .select('merchant_id')
    .eq('id', chevaletId)
    .maybeSingle()
  if (selectErr) throw selectErr
  if (!c) throw new Error(`Chevalet ${chevaletId} introuvable`)
  if (c.merchant_id) {
    await supabase.from('commercants')
      .update({ chevalet_id: null })
      .eq('merchant_id', c.merchant_id)
  }
  const { data, error } = await supabase.from('chevalets')
    .update({ merchant_id: null, status: 'disponible', installed_at: null })
    .eq('id', chevaletId)
    .select()
    .single()
  if (error) throw error
  return data
}

// ============ PRESENCES ============

export async function getPresences(chevaletId) {
  const { data, error } = await supabase
    .from('presences')
    .select('*, commercant:commercants(id, nom, zone, merchant_id, statut)')
    .eq('chevalet_id', chevaletId)
    .order('created_at')
  if (error) throw error
  return data || []
}

export async function getAllPresences() {
  const { data, error } = await supabase
    .from('presences')
    .select('*, commercant:commercants(id, nom, zone, merchant_id, statut)')
    .order('chevalet_id')
  if (error) throw error
  return data || []
}

export async function addPresence(chevaletId, commercantId) {
  const { data, error } = await supabase
    .from('presences')
    .upsert({ chevalet_id: chevaletId, commercant_id: commercantId, actif: true })
    .select()
    .single()
  if (error) throw error
  return data
}

export async function removePresence(chevaletId, commercantId) {
  const { error } = await supabase
    .from('presences')
    .delete()
    .eq('chevalet_id', chevaletId)
    .eq('commercant_id', commercantId)
  if (error) throw error
}

// ============ CONSTANTS ============

export const TYPES_COMMERCE = ['Resto', 'Coiffeur', 'Boulangerie', 'Bar/Caf\u00e9', 'Retail', 'Autre']
export const ZONES = ['IDF', 'Nice/C\u00f4te d\'Azur', 'Autre']
export const REACTIONS = [
  { value: 'chaud', label: 'Chaud', color: '#ef4444' },
  { value: 'tiede', label: 'Ti\u00e8de', color: '#f59e0b' },
  { value: 'froid', label: 'Froid', color: '#3b82f6' },
]
export const SOURCES = ['terrain', 'appel', 'referral', 'entrant', 'autre']
export const INTERACTION_TYPES = [
  { value: 'appel', label: 'Appel', icon: '\u{1F4DE}' },
  { value: 'visite', label: 'Visite', icon: '\u{1F3EA}' },
  { value: 'email', label: 'Email', icon: '\u{1F4E7}' },
  { value: 'sms', label: 'SMS', icon: '\u{1F4F1}' },
  { value: 'whatsapp', label: 'WhatsApp', icon: '\u{1F4AC}' },
  { value: 'note', label: 'Note', icon: '\u{1F4DD}' },
  { value: 'demo', label: 'D\u00e9mo', icon: '\u{1F3AF}' },
]
